import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import {
  CreditCard,
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle,
  History,
  Eye,
  Loader2,
} from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { clearServiceWorkerUserCaches } from '../../serviceWorkerRegistration';
import { reportMemberSessionExpired } from '../../services/memberApi';
import PwaInstallButton from '../shared/PwaInstallButton';

import AddStudentEmptyState from './AddStudentEmptyState';
import StudentProgressCard from './StudentProgressCard';
import AttendanceCalendar from './AttendanceCalendar';
import ResourceLibrary from './ResourceLibrary';
import TrainingProgressGraph from './TrainingProgressGraph';
import ProgressSectionNav from './ProgressSectionNav';
import LearningTab from './LearningTab';
import MemberHeader from './MemberHeader';
import MobileNavSheet from './MobileNavSheet';
import MemberMobileBottomNav from './MemberMobileBottomNav';
import DesktopNav from './DesktopNav';
import OfflineBanner from './OfflineBanner';
import ReviewBanner from './ReviewBanner';
import MemberPreferencesDialog from './preferences/MemberPreferencesDialog';
import { useMemberPreferences } from './preferences/MemberPreferencesContext';
import { useMemberWhatsNew } from './release-notes/MemberWhatsNewContext';

import useConnectivity from '@/services/connectivityService';
import { getMemberPortalStorageKey } from '@/services/memberPreferences';
import useNavFrequency from '@/hooks/useNavFrequency';
import usePortalSections from '@/hooks/usePortalSections';


const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('memberToken') || localStorage.getItem('customerToken')}`,
  'Content-Type': 'application/json'
});

// ============================================
// Subscription Status Card
// ============================================
const SubscriptionCard = ({ customer, onRefresh }) => {
  const { isOnline } = useConnectivity();
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeError, setSubscribeError] = useState(null);

  const handleSetupSubscription = async () => {
    if (!isOnline) {
      setSubscribeError('You are offline. Reconnect to set up payment.');
      return;
    }
    setSubscribing(true);
    setSubscribeError(null);

    try {
      const response = await fetch(`${API_BASE}/api/customer/create-checkout-session`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe Checkout
        window.location.href = data.checkout_url;
      } else {
        const error = await response.json();
        setSubscribeError(error.detail || 'Failed to start subscription setup');
      }
    } catch (err) {
      setSubscribeError('Something went wrong. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      none: {
        icon: Clock,
        color: 'text-gray-600 dark:text-gray-400',
        bgColor: 'bg-gray-100 dark:bg-gray-800',
        label: 'No Active Subscription',
        description: 'Set up your subscription to get started'
      },
      active: {
        icon: CheckCircle,
        color: 'text-green-600 dark:text-green-400',
        bgColor: 'bg-green-100 dark:bg-green-900/30',
        label: 'Active',
        description: 'Your subscription is active'
      },
      past_due: {
        icon: AlertTriangle,
        color: 'text-orange-600 dark:text-orange-400',
        bgColor: 'bg-orange-100 dark:bg-orange-900/30',
        label: 'Past Due',
        description: 'Please update your payment method'
      },
      paused: {
        icon: Clock,
        color: 'text-yellow-600 dark:text-yellow-400',
        bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
        label: 'Paused',
        description: 'Your subscription is paused'
      },
      cancelled: {
        icon: XCircle,
        color: 'text-red-600 dark:text-red-400',
        bgColor: 'bg-red-100 dark:bg-red-900/30',
        label: 'Cancelled',
        description: 'Your subscription has been cancelled'
      }
    };
    return configs[status] || configs.none;
  };

  const statusConfig = getStatusConfig(customer.subscription_status);
  const StatusIcon = statusConfig.icon;

  const handleCancelSubscription = async () => {
    if (!isOnline) {
      alert('You are offline. Reconnect to cancel your subscription.');
      return;
    }
    if (!window.confirm('Are you sure you want to cancel your subscription? Your subscription will remain active until the end of the billing period.')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/customer/cancel-stripe-subscription`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        onRefresh();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to cancel subscription');
      }
    } catch (err) {
      alert('Something went wrong. Please try again.');
    }
  };

  return (
    <Card className="member-card">
      <CardHeader className="border-b border-stone-100 dark:border-stone-700 bg-gradient-to-r from-transparent to-amber-50/30 dark:to-amber-900/10">
        <CardTitle className="flex items-center gap-2 text-stone-800 dark:text-stone-200">
          <CreditCard className="h-5 w-5 text-amber-600" />
          <span>Subscription</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        {/* Status Badge */}
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-full ${statusConfig.bgColor}`}>
            <StatusIcon className={`h-6 w-6 ${statusConfig.color}`} />
          </div>
          <div>
            <p className="font-semibold text-stone-800 dark:text-stone-200">{statusConfig.label}</p>
            <p className="text-sm text-stone-500 dark:text-stone-400">{statusConfig.description}</p>
          </div>
        </div>

        {/* Subscription Details */}
        {customer.subscription_status !== 'none' && (
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-stone-100">
            <div className="member-stat-card">
              <p className="text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide">Monthly Rate</p>
              <p className="font-bold text-xl text-stone-800 dark:text-stone-100 mt-1">${customer.monthly_rate}</p>
            </div>
            {customer.next_billing_date && (
              <div className="member-stat-card">
                <p className="text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide">Next Billing</p>
                <p className="font-semibold text-stone-800 dark:text-stone-100 mt-1">
                  {new Date(customer.next_billing_date).toLocaleDateString()}
                </p>
              </div>
            )}
            {customer.card_last_four && (
              <div className="member-stat-card col-span-2">
                <p className="text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide">Payment Method</p>
                <p className="font-semibold text-stone-800 dark:text-stone-100 mt-1">
                  {customer.card_brand ? `${customer.card_brand} ` : ''}
                  ****{customer.card_last_four}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="pt-4 border-t border-stone-100 space-y-3">
          {subscribeError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
              <p className="text-sm text-red-700">{subscribeError}</p>
            </div>
          )}

          {customer.subscription_status === 'none' && (
            <Button
              onClick={handleSetupSubscription}
              disabled={subscribing || !isOnline}
              variant="memberGold"
              className="w-full"
            >
              {subscribing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Setting up...
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Set Up Monthly Payment (${customer.monthly_rate}/mo)
                </>
              )}
            </Button>
          )}

          {customer.subscription_status === 'active' && (
            <Button
              variant="memberDestructive"
              size="sm"
              disabled={!isOnline}
              title={!isOnline ? 'Reconnect to take action.' : undefined}
              onClick={handleCancelSubscription}
            >
              Cancel Subscription
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================
// Billing History Card
// ============================================
const BillingHistory = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/customer/billing-history`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPayments(data);
      }
    } catch (err) {
      console.error('Failed to fetch payments:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
      pending: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
      failed: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
      refunded: 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 dark:bg-gray-800'}`}>
        {status}
      </span>
    );
  };

  return (
    <Card className="member-card">
      <CardHeader className="border-b border-stone-100 dark:border-stone-700 bg-gradient-to-r from-transparent to-amber-50/30 dark:to-amber-900/10">
        <CardTitle className="flex items-center gap-2 text-stone-800 dark:text-stone-200">
          <History className="h-5 w-5 text-amber-600" />
          <span>Billing History</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        {loading ? (
          <p className="text-center text-stone-500 dark:text-stone-400 py-4">Loading...</p>
        ) : payments.length === 0 ? (
          <p className="text-center text-stone-500 dark:text-stone-400 py-4">No payment history yet</p>
        ) : (
          <div className="space-y-3">
            {payments.map((payment) => (
              <div
                key={payment.id}
                className="flex justify-between items-center p-4 member-stat-card"
              >
                <div>
                  <p className="font-semibold text-stone-800 dark:text-stone-200">${payment.amount.toFixed(2)}</p>
                  <p className="text-sm text-stone-500 dark:text-stone-400">
                    {payment.payment_type === 'quickstart' ? 'QuickStart' : 'Monthly Subscription'}
                  </p>
                  <p className="text-xs text-stone-400">
                    {new Date(payment.created_at).toLocaleDateString()}
                  </p>
                </div>
                {getStatusBadge(payment.status)}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============================================
// Main Customer Dashboard
// ============================================
const CustomerDashboard = ({ member, onLogout, onRefresh }) => {
  const location = useLocation();
  const dashboardNavigate = useNavigate();
  const { setTheme } = useTheme();
  const {
    preferences: memberPreferences,
    loading: memberPreferencesLoading,
    saveAppearance: saveMemberAppearance,
  } = useMemberPreferences();
  const { hasUnread: hasUnreadUpdates, openWhatsNew } = useMemberWhatsNew();
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [preferencesTrigger, setPreferencesTrigger] = useState(null);
  const [appearanceSaving, setAppearanceSaving] = useState(false);
  const [appearanceError, setAppearanceError] = useState('');
  const memberIdentity = { memberId: member?.id, authType: member?.auth_type };
  const activeTabStorageKey = getMemberPortalStorageKey('activeTab', memberIdentity);
  const progressSectionStorageKey = getMemberPortalStorageKey('progressSection', memberIdentity);
  const learningSectionStorageKey = getMemberPortalStorageKey('learningSection', memberIdentity);
  const resourceSectionStorageKey = getMemberPortalStorageKey('resourceSection', memberIdentity);
  const navigationFrequencyStorageKey = getMemberPortalStorageKey('navigationFrequency', memberIdentity);

  const handleAppearanceChange = useCallback(async (appearance) => {
    if (
      memberPreferencesLoading
      || appearanceSaving
      || appearance === memberPreferences.appearance
    ) return;
    setAppearanceSaving(true);
    setAppearanceError('');
    try {
      await saveMemberAppearance(appearance);
    } catch {
      // The quick toggle updates immediately. Restore the server-backed value
      // when the write fails so the control never implies it synced across devices.
      setTheme(memberPreferences.appearance);
      setAppearanceError('Unable to save your appearance. Your previous setting was restored.');
    } finally {
      setAppearanceSaving(false);
    }
  }, [appearanceSaving, memberPreferences, memberPreferencesLoading, saveMemberAppearance, setTheme]);

  const appearanceControlDisabled = memberPreferencesLoading || appearanceSaving;

  // Restore tab states from localStorage on initial load
  const [activeTab, setActiveTab] = useState(() => {
    // Check for deep link state first (from ResourceLinkRouter)
    if (location.state?.deepLink?.tab) {
      return location.state.deepLink.tab;
    }
    const saved = activeTabStorageKey && localStorage.getItem(activeTabStorageKey);
    return saved || 'progress';
  });
  const [progressSection, setProgressSection] = useState(() => {
    const saved = progressSectionStorageKey && localStorage.getItem(progressSectionStorageKey);
    return saved || 'overview';
  });
  const [learningSection, setLearningSection] = useState(() => {
    const saved = learningSectionStorageKey && localStorage.getItem(learningSectionStorageKey);
    return saved || 'overview';
  });
  const [resourceSection, setResourceSection] = useState(() => {
    const saved = resourceSectionStorageKey && localStorage.getItem(resourceSectionStorageKey);
    return saved || 'all';
  });
  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Store deep link itemId for passing to child components
  const [deepLinkItemId, setDeepLinkItemId] = useState(
    () => location.state?.deepLink?.itemId || null
  );

  // Handle deep link navigation from ResourceLinkRouter
  useEffect(() => {
    const deepLink = location.state?.deepLink;
    if (!deepLink) return;

    if (deepLink.tab) {
      setActiveTab(deepLink.tab);
    }
    if (deepLink.itemId) {
      setDeepLinkItemId(deepLink.itemId);
    }
    if (deepLink.section) {
      switch (deepLink.tab) {
        case 'progress':
          setProgressSection(deepLink.section);
          break;
        case 'learning':
          setLearningSection(deepLink.section);
          break;
        case 'resources':
          setResourceSection(deepLink.section);
          break;
        default:
          break;
      }
    }

    // Clear location state so back/forward navigation doesn't re-trigger
    dashboardNavigate('.', { replace: true, state: {} });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist tab states to localStorage when they change
  useEffect(() => {
    if (activeTabStorageKey) localStorage.setItem(activeTabStorageKey, activeTab);
  }, [activeTab, activeTabStorageKey]);

  useEffect(() => {
    if (progressSectionStorageKey) localStorage.setItem(progressSectionStorageKey, progressSection);
  }, [progressSection, progressSectionStorageKey]);

  useEffect(() => {
    if (learningSectionStorageKey) localStorage.setItem(learningSectionStorageKey, learningSection);
  }, [learningSection, learningSectionStorageKey]);

  useEffect(() => {
    if (resourceSectionStorageKey) localStorage.setItem(resourceSectionStorageKey, resourceSection);
  }, [resourceSection, resourceSectionStorageKey]);

  // Track page visits for adaptive bottom nav (skip initial mount)
  const { recordVisit } = useNavFrequency(navigationFrequencyStorageKey);
  const isInitialLoad = useRef(true);

  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }
    const subSection = activeTab === 'progress' ? progressSection
      : activeTab === 'learning' ? learningSection
      : activeTab === 'resources' ? resourceSection
      : null;
    const pageId = subSection ? `${activeTab}:${subSection}` : activeTab;
    recordVisit(pageId);
  }, [activeTab, progressSection, learningSection, resourceSection, recordVisit]);

  // Check if this is an admin impersonation session
  const memberData = JSON.parse(localStorage.getItem('memberData') || '{}');
  const isImpersonation = memberData?.is_impersonation || false;
  const impersonatedBy = memberData?.impersonated_by || '';

  // Handler to end impersonation session
  const handleEndImpersonation = () => {
    localStorage.removeItem('memberToken');
    localStorage.removeItem('memberData');
    // Try to close the tab (works if opened via window.open)
    window.close();
    // If tab didn't close, redirect to login
    setTimeout(() => {
      window.location.href = '/login';
    }, 100);
  };

  // Determine permissions from member data
  const isStudent = member?.auth_type === 'student';
  const canViewBilling = member?.permissions?.can_view_billing !== false && !isImpersonation;
  const canViewSiblings = member?.permissions?.can_view_siblings !== false && !isImpersonation;

  // Check if content (Resources/Learning tabs) is restricted due to payment failure
  const contentRestricted = member?.content_access_restricted === true;

  // Portal section visibility (program-level + student overrides)
  const memberToken = localStorage.getItem('memberToken') || localStorage.getItem('customerToken');
  const { isSectionVisible, isLoaded: sectionsLoaded } = usePortalSections(selectedStudentId, memberToken);
  const isTabVisible = useCallback((tabId) => (
    tabId === 'progress' || !memberPreferences.navigation_hidden.includes(tabId)
  ), [memberPreferences.navigation_hidden]);

  // Redirect away from restricted or hidden tabs
  useEffect(() => {
    if (contentRestricted && (activeTab === 'resources' || activeTab === 'learning')) {
      setActiveTab('progress');
      if (activeTabStorageKey) localStorage.setItem(activeTabStorageKey, 'progress');
      return;
    }
    // Redirect if current tab is hidden by portal section config
    if (
      !isSectionVisible(activeTab)
      || !isTabVisible(activeTab)
      || (activeTab === 'billing' && !canViewBilling)
    ) {
      const fallback = memberPreferences.navigation_order.find((tab) => (
        isSectionVisible(tab)
        && isTabVisible(tab)
        && (tab !== 'billing' || canViewBilling)
        && (!contentRestricted || (tab !== 'resources' && tab !== 'learning'))
      ));
      if (fallback) {
        setActiveTab(fallback);
        if (activeTabStorageKey) localStorage.setItem(activeTabStorageKey, fallback);
      }
    }
  }, [contentRestricted, activeTab, activeTabStorageKey, isSectionVisible, isTabVisible, memberPreferences.navigation_order, canViewBilling]);

  // For backward compatibility, support both member and customer props
  const customer = member;
  const displayName = isStudent
    ? member?.name || 'Student'
    : member?.name?.split(' ')[0] || 'Member';

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/customer/students`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStudents(data);
        // Auto-select first student if available
        if (data.length > 0 && !selectedStudentId) {
          setSelectedStudentId(data[0].id);
        }
      } else if (response.status === 401) {
        reportMemberSessionExpired();
      }
    } catch (error) {
      console.error('Failed to fetch students:', error);
    } finally {
      setLoadingStudents(false);
    }
  };

  const selectedStudent = students.find(s => s.id === selectedStudentId);

  // Get current sub-section based on active tab
  const getCurrentSubSection = () => {
    switch (activeTab) {
      case 'progress': return progressSection;
      case 'learning': return learningSection;
      case 'resources': return resourceSection;
      default: return null;
    }
  };

  // Handle sub-section change based on active tab
  const handleSubSectionChange = (sectionId) => {
    switch (activeTab) {
      case 'progress':
        setProgressSection(sectionId);
        break;
      case 'learning':
        setLearningSection(sectionId);
        break;
      case 'resources':
        setResourceSection(sectionId);
        break;
    }
  };

  return (
    <div className="member-bg min-h-screen">
      {/* Impersonation Banner */}
      {isImpersonation && (
        <div
          className="bg-purple-600 text-white px-4 z-[70] flex items-center"
          style={{ height: '36px', position: 'sticky', top: 0 }}
        >
          <div className="max-w-6xl mx-auto w-full flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              <span className="font-medium text-sm">
                Admin View — {member?.name || 'Student'}
              </span>
              {impersonatedBy && (
                <span className="text-purple-200 text-xs hidden sm:inline">
                  (by {impersonatedBy})
                </span>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-purple-700 hover:text-white text-xs px-2 py-1 h-7"
              onClick={handleEndImpersonation}
            >
              End Session
            </Button>
          </div>
        </div>
      )}

      <OfflineBanner />

      {appearanceError && (
        <div className="border-b border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200" role="alert">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-3">
            <span>{appearanceError}</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="shrink-0 text-current"
              onClick={() => setAppearanceError('')}
            >
              Dismiss
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <MemberHeader
        student={selectedStudent}
        students={canViewSiblings ? students : []}
        customerName={displayName}
        onSelectStudent={setSelectedStudentId}
        onMenuClick={() => setMobileNavOpen(true)}
        onLogout={() => { clearServiceWorkerUserCaches('members'); onLogout(); }}
        isImpersonation={isImpersonation}
        isStudent={isStudent}
        onOpenPreferences={(trigger) => { setPreferencesTrigger(trigger); setPreferencesOpen(true); }}
        onOpenWhatsNew={openWhatsNew}
        hasUnreadUpdates={hasUnreadUpdates}
        onAppearanceChange={handleAppearanceChange}
        appearanceSaving={appearanceControlDisabled}
      />

      {/* Desktop Navigation */}
      <DesktopNav
        activeTab={activeTab}
        onTabChange={setActiveTab}
        subSection={getCurrentSubSection()}
        onSubSectionChange={handleSubSectionChange}
        canViewBilling={canViewBilling}
        contentRestricted={contentRestricted}
        isSectionVisible={isSectionVisible}
        isLoaded={sectionsLoaded}
        navigationPreferences={memberPreferences}
      />

      {/* Mobile Navigation Sheet */}
      <MobileNavSheet
        open={mobileNavOpen}
        onOpenChange={setMobileNavOpen}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        subSection={getCurrentSubSection()}
        onSubSectionChange={handleSubSectionChange}
        canViewBilling={canViewBilling}
        contentRestricted={contentRestricted}
        isSectionVisible={isSectionVisible}
        isLoaded={sectionsLoaded}
        onLogout={() => { clearServiceWorkerUserCaches('members'); onLogout(); }}
        navigationPreferences={memberPreferences}
        onOpenPreferences={() => { setPreferencesTrigger(null); setPreferencesOpen(true); }}
        onOpenWhatsNew={() => openWhatsNew()}
        hasUnreadUpdates={hasUnreadUpdates}
        onAppearanceChange={handleAppearanceChange}
        appearanceSaving={appearanceControlDisabled}
      />

      {/* Mobile Bottom Nav */}
      <MemberMobileBottomNav
        activeTab={activeTab}
        subSection={getCurrentSubSection()}
        onTabChange={setActiveTab}
        onSubSectionChange={handleSubSectionChange}
        canViewBilling={canViewBilling}
        contentRestricted={contentRestricted}
        isSectionVisible={isSectionVisible}
        isLoaded={sectionsLoaded}
        navigationPreferences={memberPreferences}
        navigationFrequencyStorageKey={navigationFrequencyStorageKey}
      />

      <MemberPreferencesDialog
        open={preferencesOpen}
        onOpenChange={setPreferencesOpen}
        returnFocusTarget={preferencesTrigger}
      />

      {/* Content Area */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-6">
        {/* Google review reminder — first thing a member sees, dismissible */}
        {!isImpersonation && <ReviewBanner />}

        <div className="mb-4 flex justify-end">
          <PwaInstallButton variant="member">Install Members App</PwaInstallButton>
        </div>

        {/* Progress Tab */}
        {activeTab === 'progress' && (
          <div className="space-y-6">
            {loadingStudents ? (
              <Card>
                <CardContent className="py-8 text-center text-gray-500 dark:text-gray-400">
                  Loading students...
                </CardContent>
              </Card>
            ) : students.length === 0 ? (
              <AddStudentEmptyState onStudentApproved={fetchStudents} />
            ) : selectedStudent && (
              <ProgressSectionNav
                studentId={selectedStudent.id}
                activeSection={progressSection}
                onSectionChange={setProgressSection}
                isSectionVisible={isSectionVisible}
                hideNav
              >
                {progressSection === 'overview' && (
                  <StudentProgressCard student={selectedStudent} />
                )}
                {progressSection === 'calendar' && (
                  <AttendanceCalendar student={selectedStudent} />
                )}
                {progressSection === 'training' && (
                  <TrainingProgressGraph student={selectedStudent} />
                )}
                {progressSection === 'journey' && (
                  <StudentProgressCard student={selectedStudent} showJourneyOnly />
                )}
              </ProgressSectionNav>
            )}
          </div>
        )}

        {/* Resources Tab */}
        {activeTab === 'resources' && (
          <div className="space-y-6">
            {loadingStudents ? (
              <Card>
                <CardContent className="py-8 text-center text-gray-500 dark:text-gray-400">
                  Loading...
                </CardContent>
              </Card>
            ) : students.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <p className="text-gray-600 dark:text-gray-400">No students found on your account.</p>
                </CardContent>
              </Card>
            ) : selectedStudent && (
              <ResourceLibrary
                student={selectedStudent}
                activeSection={resourceSection}
                onSectionChange={setResourceSection}
                hideNav
                focusResourceId={activeTab === 'resources' ? deepLinkItemId : null}
              />
            )}
          </div>
        )}

        {/* Learning Tab */}
        {activeTab === 'learning' && (
          <div className="space-y-6">
            {loadingStudents ? (
              <Card>
                <CardContent className="py-8 text-center text-gray-500 dark:text-gray-400">
                  Loading...
                </CardContent>
              </Card>
            ) : students.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <p className="text-gray-600 dark:text-gray-400">No students found on your account.</p>
                </CardContent>
              </Card>
            ) : selectedStudent && (
              <LearningTab
                student={selectedStudent}
                token={localStorage.getItem('memberToken') || localStorage.getItem('customerToken')}
                activeSection={learningSection}
                onSectionChange={setLearningSection}
                isSectionVisible={isSectionVisible}
                hideNav
              />
            )}
          </div>
        )}

        {/* Billing Tab */}
        {activeTab === 'billing' && canViewBilling && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <SubscriptionCard customer={customer} onRefresh={onRefresh} />
            <BillingHistory />
          </div>
        )}

        {/* Contact Info */}
        <Card className="member-card mt-8">
          <CardContent className="py-6">
            <div className="text-center">
              <p className="text-stone-500 dark:text-stone-400 mb-2 font-dm-sans">
                Questions about your membership?
              </p>
              <p className="font-medium text-stone-800 dark:text-stone-200 font-dm-sans">
                Contact us at{' '}
                <a href="mailto:info@owazamartialarts.com" className="text-[#9B1B30] hover:text-[#6B1220] hover:underline transition-colors">
                  info@owazamartialarts.com
                </a>
                {' '}or call{' '}
                <a href="tel:+13524280086" className="text-[#9B1B30] hover:text-[#6B1220] hover:underline transition-colors">
                  (352) 428-0086
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default CustomerDashboard;
